import {
  ContentNodeUtils,
  DataTypeLocator,
  ListContentNode,
  ObjectContentNode,
  QueryConfig,
  QueryNode,
  ViewConfig,
  WidgetConfig,
} from '@helo/cms'

import { ProbeStatus } from '../ListTablesTestPage/ListTablesTestPage.parts'

export type { ProbeStatus }

export type ParentListSource = {
  parentListQueryId: string
  entityIdDataKey?: string
  rawEntityIdPath: string[]
}

export type DetailSubTableTarget = {
  viewId: string
  viewLabel: string
  viewPath: string
  queryId: string
  connectionLocator: DataTypeLocator
  queryConfig: QueryConfig
  parentListSource: ParentListSource
  sampleColumnSelector?: DataTypeLocator
}

export type DetailSubTableProbeRow = DetailSubTableTarget & {
  probeQueryId: string
  status: ProbeStatus
  parentEntityId?: string
  totalCount?: number
  itemCount?: number
  sampleValue?: string
  recordsChecked?: number
  passRecordPath?: string
  errorMessage?: string
}

const PARENT_LIST_BY_DETAIL_VIEW: Record<string, ParentListSource> = {
  containerDetailsView: {
    parentListQueryId: 'containersList',
    entityIdDataKey: 'containerIRI',
    rawEntityIdPath: [
      'electrumpython',
      'TransportUnitShipmentDocument_list',
      'items',
      '0',
      'transportUnitShipmentDocumentPk',
    ],
  },
  fclContainerDetailsView: {
    parentListQueryId: 'fclContainersList',
    entityIdDataKey: 'containerIRI',
    rawEntityIdPath: [
      'electrumpython',
      'TransportUnitCluster_list',
      'items',
      '0',
      'transportUnitClusterPk',
    ],
  },
  shipmentDetailView: {
    parentListQueryId: 'shipmentsList',
    entityIdDataKey: 'shipmentDocumentPk',
    rawEntityIdPath: [
      'electrumpython',
      'ShipmentDocument_list',
      'items',
      '0',
      'shipmentDocumentPk',
    ],
  },
  orderDetailView: {
    parentListQueryId: 'ordersList',
    entityIdDataKey: 'purchaseOrderPk',
    rawEntityIdPath: [
      'electrumpython',
      'PurchaseOrder_list',
      'items',
      '0',
      'purchaseOrderPk',
    ],
  },
  itemDetailView: {
    parentListQueryId: 'itemsList',
    entityIdDataKey: 'catalogItemPk',
    rawEntityIdPath: [
      'electrumpython',
      'CatalogItem_list',
      'items',
      '0',
      'catalogItemPk',
    ],
  },
}

const isDataTypeLocator = (value: unknown): value is DataTypeLocator =>
  Array.isArray(value) &&
  value.every(
    segment =>
      Array.isArray(segment) &&
      segment.length === 2 &&
      typeof segment[0] === 'string' &&
      typeof segment[1] === 'string',
  )

const getWidgetQueryId = (widget: WidgetConfig): string | undefined => {
  const queryId = widget.state?.widgetProps?.queryId
  return typeof queryId === 'string' && queryId.length > 0 ? queryId : undefined
}

const walkWidget = (
  widget: WidgetConfig,
  visit: (widget: WidgetConfig) => void,
): void => {
  visit(widget)
  widget.children?.forEach(child => walkWidget(child, visit))
  if (widget.slots) {
    Object.values(widget.slots).forEach(slot => walkWidget(slot, visit))
  }
}

const findTableWidget = (
  view: ViewConfig,
  queryId: string,
): WidgetConfig | undefined => {
  let match: WidgetConfig | undefined

  view.widgets.forEach(widget =>
    walkWidget(widget, candidate => {
      if (match || candidate.type !== 'Table') {
        return
      }

      if (getWidgetQueryId(candidate) === queryId) {
        match = candidate
      }
    }),
  )

  return match
}

const getSampleColumnSelector = (
  widget: WidgetConfig,
): DataTypeLocator | undefined => {
  const tableStateConfig = widget.state?.widgetProps?.tableStateConfig as
    | {
        columnConfigs?: Array<{
          selector?: DataTypeLocator
          config?: { selector?: DataTypeLocator }
        }>
      }
    | undefined

  const columnConfigs = tableStateConfig?.columnConfigs ?? []
  const firstColumn = columnConfigs.find(column => {
    const selector = column.selector ?? column.config?.selector
    return isDataTypeLocator(selector)
  })

  return firstColumn?.selector ?? firstColumn?.config?.selector
}

const hasUrlParamsIdVariable = (
  variables: QueryConfig['variables'],
): boolean => {
  const idVariable = variables?.id as
    | { kind?: string; value?: unknown }
    | undefined

  return (
    idVariable?.kind === 'REFERENCE' &&
    Array.isArray(idVariable.value) &&
    idVariable.value[0] === 'urlParams' &&
    idVariable.value[1] === 'id'
  )
}

const getValueAtPath = (value: unknown, path: string[]): unknown => {
  return path.reduce<unknown>((current, segment) => {
    if (current === null || current === undefined) {
      return undefined
    }

    if (Array.isArray(current) && /^\d+$/.test(segment)) {
      return current[Number(segment)]
    }

    if (typeof current === 'object') {
      return (current as Record<string, unknown>)[segment]
    }

    return undefined
  }, value)
}

const extractEntityIdFromMappedRow = (
  queryNode: QueryNode,
  connectionLocator: DataTypeLocator,
  entityIdDataKey: string,
): string | undefined =>
  extractEntityIdsFromMappedPage(
    queryNode,
    connectionLocator,
    entityIdDataKey,
  )[0]

const extractEntityIdsFromMappedPage = (
  queryNode: QueryNode | undefined,
  connectionLocator: DataTypeLocator,
  entityIdDataKey: string,
): string[] => {
  if (!queryNode?.queryContentNode) {
    return []
  }

  const connectionContentNode = ContentNodeUtils.findContentNode(
    connectionLocator,
  )(queryNode.queryContentNode) as ObjectContentNode | null

  if (!connectionContentNode) {
    return []
  }

  const itemsListContentNode = ContentNodeUtils.findContentNode([
    ['key', 'items'],
    ['typeName', 'List'],
  ])(connectionContentNode) as ListContentNode | null

  return (itemsListContentNode?.items ?? [])
    .map(row => {
      const entityId = (row as ObjectContentNode | undefined)?.data?.[
        entityIdDataKey
      ]

      return typeof entityId === 'string' && entityId.length > 0
        ? entityId
        : undefined
    })
    .filter((entityId): entityId is string => Boolean(entityId))
}

const extractEntityIdFromRawResponse = (
  queryNode: QueryNode,
  rawEntityIdPath: string[],
): string | undefined => {
  const entityId = getValueAtPath(queryNode.apolloState?.data, rawEntityIdPath)

  return typeof entityId === 'string' && entityId.length > 0
    ? entityId
    : undefined
}

const createParentListProbeQueryId = (parentListQueryId: string): string =>
  `detailSubTableParent_${parentListQueryId}`

const createProbeQueryId = (queryId: string): string =>
  `detailSubTableTest_${queryId}`

const collectDetailSubTables = (
  viewConfigs: ViewConfig[],
): DetailSubTableTarget[] =>
  viewConfigs
    .filter(view => view.state.path.includes(':'))
    .flatMap(view => {
      const parentListSource = PARENT_LIST_BY_DETAIL_VIEW[view.id]

      if (!parentListSource) {
        return []
      }

      return view.queries
        .filter(
          query => query.paginated && hasUrlParamsIdVariable(query.variables),
        )
        .flatMap(query => {
          const tableWidget = findTableWidget(view, query.id)
          const connectionLocator =
            tableWidget?.state?.widgetProps?.connectionLocator

          if (!isDataTypeLocator(connectionLocator)) {
            return []
          }

          return [
            {
              viewId: view.id,
              viewLabel: view.state.label,
              viewPath: view.state.path,
              queryId: query.id,
              connectionLocator,
              queryConfig: query,
              parentListSource,
              sampleColumnSelector: tableWidget
                ? getSampleColumnSelector(tableWidget)
                : undefined,
            },
          ]
        })
    })
    .sort((left, right) => {
      const viewCompare = left.viewLabel.localeCompare(right.viewLabel)

      if (viewCompare !== 0) {
        return viewCompare
      }

      return left.queryId.localeCompare(right.queryId)
    })

const toInitialProbeRows = (
  targets: DetailSubTableTarget[],
): DetailSubTableProbeRow[] =>
  targets.map(target => ({
    ...target,
    probeQueryId: createProbeQueryId(target.queryId),
    status: 'running',
  }))

export const DetailSubTablesTest = {
  PARENT_LIST_BY_DETAIL_VIEW,
  collectDetailSubTables,
  createParentListProbeQueryId,
  createProbeQueryId,
  extractEntityIdFromMappedRow,
  extractEntityIdsFromMappedPage,
  extractEntityIdFromRawResponse,
  toInitialProbeRows,
}
